import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Building,
  Plus,
  Users,
  Home,
  Settings,
  CheckCircle,
  AlertTriangle,
  UserPlus,
  UserMinus,
  RefreshCw,
  Wrench,
  Zap,
  Droplet,
  Paintbrush,
  Clock,
  DollarSign,
  Edit,
  Trash2,
  MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoomManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedResident, setSelectedResident] = useState('');
  
  const [formData, setFormData] = useState({
    room_number: '',
    capacity: 1,
    gender: 'any',
    notes: ''
  });

  const [maintenanceFormData, setMaintenanceFormData] = useState({
    maintenance_type: '',
    priority: 'medium',
    description: '',
    estimated_duration: '',
    materials_needed: '',
    cost_estimate: ''
  });

  useEffect(() => {
    fetchRooms();
    fetchResidents();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to load rooms');
    }
  };

  const fetchResidents = async () => {
    try {
      const response = await axios.get(`${API}/residents?status=current`);
      setResidents(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching residents:', error);
      toast.error('Failed to load residents');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/rooms`, formData);
      toast.success('Room added successfully');
      resetForm();
      fetchRooms();
    } catch (error) {
      console.error('Error creating room:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402) {
        toast.error(typeof detail === 'object' ? detail.message : 'Bed limit reached — upgrade your plan to add more beds.', {
          action: { label: 'Upgrade', onClick: () => { window.location.href = '/billing'; } },
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create room');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      room_number: '',
      capacity: 1,
      gender: 'any',
      notes: ''
    });
    setShowAddDialog(false);
  };

  const getAge = (dob) => {
    if (!dob) return null;
    const b = new Date(dob);
    if (isNaN(b.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - b.getFullYear();
    const m = now.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
    return age >= 0 && age <= 130 ? age : null;
  };

  const handleAssignResident = async () => {
    if (!selectedResident || !selectedRoom) {
      toast.error('Please select both a resident and a room');
      return;
    }

    // Minor Room Guard — warn before placing an unaccompanied minor (child-safety compliance)
    const resObj = residents.find(r => r.id === selectedResident);
    const age = resObj ? getAge(resObj.date_of_birth) : null;
    if (age !== null && age < 18) {
      const occupants = residents.filter(r => r.room_id === selectedRoom.id && r.id !== selectedResident);
      const hasAdult = occupants.some(o => { const a = getAge(o.date_of_birth); return a !== null && a >= 18; });
      if (!hasAdult) {
        const ok = window.confirm(
          `Child-safety check: ${resObj.first_name} ${resObj.last_name} is a minor (age ${age}) and Room ${selectedRoom.room_number} has no accompanying adult.\n\nPlace this minor here anyway?`
        );
        if (!ok) return;
      }
    }
    
    try {
      console.log('Assigning resident:', selectedResident, 'to room:', selectedRoom.id);
      await axios.put(`${API}/rooms/${selectedRoom.id}/assign/${selectedResident}`);
      toast.success('Resident assigned to room successfully');
      setShowAssignDialog(false);
      setSelectedResident('');
      setSelectedRoom(null);
      fetchRooms();
      fetchResidents();
    } catch (error) {
      console.error('Error assigning resident:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to assign resident to room';
      toast.error(`Assignment failed: ${errorMessage}`);
    }
  };

  const handleUnassignResident = async (roomId, residentId) => {
    try {
      await axios.put(`${API}/rooms/${roomId}/unassign/${residentId}`);
      toast.success('Resident unassigned from room');
      fetchRooms();
      fetchResidents();
    } catch (error) {
      console.error('Error unassigning resident:', error);
      toast.error('Failed to unassign resident');
    }
  };

  const handleRoomStatusChange = async (roomId, newStatus) => {
    try {
      await axios.put(`${API}/rooms/${roomId}/status`, { status: newStatus });
      toast.success(`Room status updated to ${newStatus}`);
      fetchRooms();
    } catch (error) {
      console.error('Error updating room status:', error);
      toast.error('Failed to update room status');
    }
  };

  const handleSyncOccupancy = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/rooms/sync-occupancy`);
      toast.success('Room occupancies synchronized successfully');
      fetchRooms();
    } catch (error) {
      console.error('Error syncing occupancy:', error);
      toast.error('Failed to sync room occupancies');
    } finally {
      setLoading(false);
    }
  };

  const handleMaintenanceRequest = async () => {
    if (!selectedRoom || !maintenanceFormData.maintenance_type || !maintenanceFormData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const requestData = {
        room_id: selectedRoom.id,
        maintenance_type: maintenanceFormData.maintenance_type,
        priority: maintenanceFormData.priority,
        description: maintenanceFormData.description,
        estimated_duration: maintenanceFormData.estimated_duration || null,
        materials_needed: maintenanceFormData.materials_needed || null,
        cost_estimate: maintenanceFormData.cost_estimate ? parseFloat(maintenanceFormData.cost_estimate) : null
      };

      await axios.post(`${API}/maintenance-requests`, requestData);
      toast.success(`Maintenance request created for Room ${selectedRoom.room_number}`);
      resetMaintenanceForm();
      fetchRooms();
    } catch (error) {
      console.error('Error creating maintenance request:', error);
      toast.error('Failed to create maintenance request');
    }
  };

  const resetMaintenanceForm = () => {
    setMaintenanceFormData({
      maintenance_type: '',
      priority: 'medium',
      description: '',
      estimated_duration: '',
      materials_needed: '',
      cost_estimate: ''
    });
    setShowMaintenanceDialog(false);
    setSelectedRoom(null);
  };

  const handleEditRoom = async () => {
    if (!formData.room_number || formData.capacity < 1) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.put(`${API}/rooms/${selectedRoom.id}`, {
        room_number: formData.room_number,
        capacity: formData.capacity,
        gender: formData.gender,
        notes: formData.notes
      });
      toast.success(`Room ${formData.room_number} updated successfully`);
      resetForm();
      fetchRooms();
    } catch (error) {
      console.error('Error updating room:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402) {
        toast.error(typeof detail === 'object' ? detail.message : 'Bed limit reached — upgrade your plan to add more beds.', {
          action: { label: 'Upgrade', onClick: () => { window.location.href = '/billing'; } },
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to update room');
      }
    }
  };

  const handleDeleteRoom = async (room) => {
    if (room.current_occupancy > 0) {
      toast.error('Cannot delete room with assigned residents');
      return;
    }

    // Open confirmation dialog instead of window.confirm
    setRoomToDelete(room);
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteRoom = async () => {
    if (!roomToDelete) return;

    try {
      await axios.delete(`${API}/rooms/${roomToDelete.id}`);
      toast.success(`Room ${roomToDelete.room_number} deleted successfully`);
      setShowDeleteConfirmDialog(false);
      setRoomToDelete(null);
      fetchRooms();
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error('Failed to delete room');
      setShowDeleteConfirmDialog(false);
      setRoomToDelete(null);
    }
  };

  const cancelDeleteRoom = () => {
    setShowDeleteConfirmDialog(false);
    setRoomToDelete(null);
  };

  const openEditDialog = (room) => {
    setSelectedRoom(room);
    setFormData({
      room_number: room.room_number,
      capacity: room.capacity,
      gender: room.gender || 'any',
      notes: room.notes || ''
    });
    setShowEditDialog(true);
  };

  // Gender designation styling for rooms and bed tiles
  const GENDER_META = {
    male: { label: 'Male', badge: 'bg-blue-100 text-blue-800 border-blue-200', tileFree: 'bg-blue-100 border-blue-300 text-blue-700', tileFull: 'bg-blue-600 border-blue-700 text-white' },
    female: { label: 'Female', badge: 'bg-pink-100 text-pink-800 border-pink-200', tileFree: 'bg-pink-100 border-pink-300 text-pink-700', tileFull: 'bg-pink-600 border-pink-700 text-white' },
    family: { label: 'Family', badge: 'bg-purple-100 text-purple-800 border-purple-200', tileFree: 'bg-purple-100 border-purple-300 text-purple-700', tileFull: 'bg-purple-600 border-purple-700 text-white' },
    any: { label: 'Any', badge: 'bg-gray-100 text-gray-800 border-gray-200', tileFree: 'bg-gray-100 border-gray-300 text-gray-600', tileFull: 'bg-gray-600 border-gray-700 text-white' },
  };
  const genderMeta = (g) => GENDER_META[g] || GENDER_META.any;

  // Whether a resident may occupy this room given its gender designation.
  // Trans MTF identifies female, trans FTM identifies male.
  const genderAllows = (room, resident) => {
    if (!room || room.gender === 'any' || room.gender === 'family') return true;
    const map = {
      male: 'male', transgender_ftm: 'male', transgender_male: 'male',
      female: 'female', transgender_mtf: 'female', transgender_female: 'female',
    };
    const eff = map[(resident.gender || '').toLowerCase()];
    if (!eff) return true; // non-binary/other/declined/unspecified: staff discretion
    return eff === room.gender;
  };

  const getRoomStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'room-available text-white';
      case 'occupied':
        return 'room-occupied text-white';
      case 'maintenance':
        return 'room-maintenance text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getRoomStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return CheckCircle;
      case 'occupied':
        return Users;
      case 'maintenance':
        return Settings;
      default:
        return AlertTriangle;
    }
  };

  const getOccupancyColor = (current, capacity) => {
    const percentage = (current / capacity) * 100;
    if (percentage === 0) return 'text-green-600';
    if (percentage < 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Room Management</h1>
            <p className="text-gray-600">Manage room assignments and availability</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-32 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <Building className="h-8 w-8 text-indigo-600" />
            <span>Room Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Manage room assignments and availability</p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Button 
            onClick={handleSyncOccupancy}
            variant="outline"
            disabled={loading}
            className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Sync Occupancy
          </Button>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Add Room</span>
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Room</DialogTitle>
              <DialogDescription>
                Create a new room for the shelter
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Room Number *</label>
                <Input
                  required
                  value={formData.room_number}
                  onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                  placeholder="e.g., 101, A-12, Main-01"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Capacity *</label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  required
                  value={formData.capacity}
                  onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})}
                  placeholder="Number of beds"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Room Gender *</label>
                <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                  <SelectTrigger data-testid="add-room-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="any">Any / Mixed</SelectItem>
                    <SelectItem value="family">Family</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Beds are color-coded by this designation and assignment is restricted for Male/Female rooms.</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any special notes about this room..."
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                  Add Room
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Room Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Rooms</p>
                <p className="text-2xl font-bold text-gray-900">{rooms.length}</p>
              </div>
              <Building className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Available Beds</p>
                <p className="text-2xl font-bold text-green-600">
                  {rooms.reduce((total, room) => total + (room.capacity - room.current_occupancy), 0)}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow border-l-4 border-l-blue-500" data-testid="male-beds-available">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Male Beds Free</p>
                <p className="text-2xl font-bold text-blue-600">
                  {rooms.filter(r => r.gender === 'male').reduce((t, r) => t + (r.capacity - r.current_occupancy), 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow border-l-4 border-l-pink-500" data-testid="female-beds-available">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Female Beds Free</p>
                <p className="text-2xl font-bold text-pink-600">
                  {rooms.filter(r => r.gender === 'female').reduce((t, r) => t + (r.capacity - r.current_occupancy), 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-pink-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Occupied Beds</p>
                <p className="text-2xl font-bold text-orange-600">
                  {rooms.reduce((total, room) => total + room.current_occupancy, 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Maintenance</p>
                <p className="text-2xl font-bold text-red-600">
                  {rooms.filter(room => room.status === 'maintenance').length}
                </p>
              </div>
              <Settings className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rooms Grid */}
      {rooms.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <Building className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No rooms created yet</h3>
            <p className="text-gray-600 mb-4">Start by adding your first room to the shelter.</p>
            <Button onClick={() => setShowAddDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-2" />
              Add First Room
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {rooms.map((room) => {
            const StatusIcon = getRoomStatusIcon(room.status);
            const assignedResidents = residents.filter(r => r.room_id === room.id);
            
            return (
              <Card key={room.id} className="card-shadow hover:card-shadow-lg transition-shadow">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center space-x-2">
                      <Home className="h-5 w-5 text-gray-600" />
                      <span>Room {room.room_number}</span>
                    </CardTitle>
                    <div className="flex flex-col items-end space-y-1">
                      <div className="flex items-center gap-1">
                        <Badge className={`px-2 py-0.5 border ${genderMeta(room.gender).badge}`} data-testid={`room-gender-${room.id}`}>
                          {genderMeta(room.gender).label}
                        </Badge>
                        <Badge className={`px-3 py-1 ${getRoomStatusColor(room.status)}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {room.status === 'maintenance' ? 'Maintenance' : 
                           room.current_occupancy >= room.capacity ? 'Full' : 
                           room.current_occupancy > 0 ? 'Partial' : 'Empty'}
                        </Badge>
                      </div>
                      <span className="text-xs text-gray-500">
                        {room.capacity - room.current_occupancy} beds available
                      </span>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Occupancy Info */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Users className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Occupancy</span>
                    </div>
                    <span className={`text-sm font-bold ${getOccupancyColor(room.current_occupancy, room.capacity)}`}>
                      {room.current_occupancy} / {room.capacity}
                    </span>
                  </div>

                  {/* Bed tiles — color-coded by room gender */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Beds</p>
                    <div className="flex flex-wrap gap-1.5" data-testid={`bed-tiles-${room.id}`}>
                      {Array.from({ length: room.capacity }).map((_, i) => {
                        const occupied = i < room.current_occupancy;
                        const meta = genderMeta(room.gender);
                        return (
                          <div
                            key={i}
                            title={`${meta.label} bed ${i + 1} — ${occupied ? 'Occupied' : 'Available'}`}
                            className={`h-8 w-8 rounded-md border flex items-center justify-center text-xs font-semibold ${occupied ? meta.tileFull : meta.tileFree}`}
                            data-testid={`bed-${room.id}-${i}`}
                          >
                            {occupied ? '●' : i + 1}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Assigned Residents */}
                  {assignedResidents.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Assigned Residents:</p>
                      <div className="space-y-1">
                        {assignedResidents.map((resident) => (
                          <div key={resident.id} className="flex items-center space-x-2 p-2 bg-blue-50 rounded text-sm">
                            <Users className="h-3 w-3 text-blue-600" />
                            <span className="text-blue-900">
                              {resident.first_name} {resident.last_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Room Notes */}
                  {room.notes && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-yellow-900">Room Notes</p>
                          <p className="text-sm text-yellow-700">{room.notes}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Room Actions */}
                  <div className="pt-4 border-t space-y-2">
                    {room.status !== 'maintenance' && room.current_occupancy < room.capacity && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedRoom(room);
                          setShowAssignDialog(true);
                        }}
                        className="w-full text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Assign Resident
                      </Button>
                    )}
                    
                    {room.status !== 'maintenance' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedRoom(room);
                          setShowMaintenanceDialog(true);
                        }}
                        className="w-full text-orange-600 hover:text-orange-700 border-orange-200 hover:border-orange-300"
                      >
                        <Wrench className="h-4 w-4 mr-2" />
                        Request Maintenance
                      </Button>
                    )}
                    
                    {room.status === 'maintenance' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRoomStatusChange(room.id, 'available')}
                        className="w-full text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mark as Available
                      </Button>
                    )}

                    {/* Edit and Delete buttons */}
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(room)}
                        className="flex-1 text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Room
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteRoom(room)}
                        className="flex-1 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                        disabled={room.current_occupancy > 0}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>

                    {/* Unassign residents */}
                    {assignedResidents.map((resident) => {
                      const a = getAge(resident.date_of_birth);
                      return (
                        <div key={`occ-${resident.id}`} className="space-y-0.5">
                          {(resident.household_id || (a !== null && a < 18)) && (
                            <div className="flex flex-wrap gap-1 px-1">
                              {a !== null && a < 18 && (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">Minor · {a}</span>
                              )}
                              {resident.household_id && (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800">👪 {resident.relationship_to_head || 'household'}</span>
                              )}
                            </div>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUnassignResident(room.id, resident.id)}
                            className="w-full text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                          >
                            <UserMinus className="h-4 w-4 mr-2" />
                            Unassign {resident.first_name}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Assign Resident Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Resident to Room</DialogTitle>
            <DialogDescription>
              Assign a resident to {selectedRoom ? `Room ${selectedRoom.room_number}` : 'this room'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedRoom && (selectedRoom.gender === 'male' || selectedRoom.gender === 'female') && (
              <div className={`text-sm rounded-md p-2 border ${genderMeta(selectedRoom.gender).badge}`} data-testid="assign-gender-note">
                This is a <strong>{genderMeta(selectedRoom.gender).label}</strong> room — only {genderMeta(selectedRoom.gender).label.toLowerCase()} residents are shown.
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700">Select Resident</label>
              <Select value={selectedResident} onValueChange={setSelectedResident}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a resident..." />
                </SelectTrigger>
                <SelectContent>
                  {residents
                    .filter(r => r.status === 'current' && !r.room_id && genderAllows(selectedRoom, r))
                    .map((resident) => {
                      const a = getAge(resident.date_of_birth);
                      return (
                        <SelectItem key={resident.id} value={resident.id}>
                          {resident.first_name} {resident.last_name}
                          {a !== null && a < 18 ? ` · Minor (${a})` : ''}
                          {resident.household_id ? ' · 👪' : ''}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>

            {/* Family Room Match — suggest rooms that fit the whole household (staff can still split them) */}
            {(() => {
              const resObj = residents.find(r => r.id === selectedResident);
              if (!resObj || !resObj.household_id) return null;
              const members = residents.filter(r => r.household_id === resObj.household_id);
              const needing = members.filter(r => !r.room_id || r.id === selectedResident);
              const openHere = selectedRoom ? (selectedRoom.capacity - selectedRoom.current_occupancy) : 0;
              const fitsHere = openHere >= needing.length;
              const fitRooms = rooms.filter(rm => rm.status !== 'maintenance'
                && (rm.capacity - rm.current_occupancy) >= needing.length
                && genderAllows(rm, resObj));
              return (
                <div className="text-sm rounded-md p-3 border border-indigo-200 bg-indigo-50 space-y-1.5" data-testid="family-room-match">
                  <div className="font-medium text-indigo-900">👪 Part of a household — {members.length} member(s), {needing.length} still need a bed.</div>
                  {selectedRoom && (
                    <div className={fitsHere ? 'text-emerald-700' : 'text-amber-700'}>
                      Room {selectedRoom.room_number} has {openHere} open bed(s) — {fitsHere ? 'fits the whole household ✓' : 'not enough for everyone.'}
                    </div>
                  )}
                  {fitRooms.length > 0 && (
                    <div className="text-gray-700">
                      Rooms that fit all {needing.length}: {fitRooms.map(rm => `Room ${rm.room_number} (${rm.capacity - rm.current_occupancy} free)`).join(', ')}
                    </div>
                  )}
                  <div className="text-xs text-gray-500">You can assign members individually to split the family across rooms if needed.</div>
                </div>
              );
            })()}
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => {
                setShowAssignDialog(false);
                setSelectedResident('');
                setSelectedRoom(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssignResident}
                disabled={!selectedResident}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Assign Resident
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Maintenance Request Dialog */}
      <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Wrench className="h-5 w-5 text-orange-600" />
              <span>Request Maintenance</span>
            </DialogTitle>
            <DialogDescription>
              Create a maintenance request for {selectedRoom ? `Room ${selectedRoom.room_number}` : 'this room'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Maintenance Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Maintenance Type *</label>
                <Select 
                  value={maintenanceFormData.maintenance_type} 
                  onValueChange={(value) => setMaintenanceFormData({...maintenanceFormData, maintenance_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select maintenance type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plumbing">
                      <div className="flex items-center space-x-2">
                        <Droplet className="h-4 w-4" />
                        <span>Plumbing</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="electrical">
                      <div className="flex items-center space-x-2">
                        <Zap className="h-4 w-4" />
                        <span>Electrical</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="hvac">HVAC (Heating/Cooling)</SelectItem>
                    <SelectItem value="cleaning">
                      <div className="flex items-center space-x-2">
                        <Paintbrush className="h-4 w-4" />
                        <span>Deep Cleaning</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="painting">Painting</SelectItem>
                    <SelectItem value="flooring">Flooring</SelectItem>
                    <SelectItem value="windows_doors">Windows & Doors</SelectItem>
                    <SelectItem value="appliances">Appliances</SelectItem>
                    <SelectItem value="furniture">Furniture</SelectItem>
                    <SelectItem value="safety_security">Safety & Security</SelectItem>
                    <SelectItem value="pest_control">Pest Control</SelectItem>
                    <SelectItem value="general_repairs">General Repairs</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Priority *</label>
                <Select 
                  value={maintenanceFormData.priority} 
                  onValueChange={(value) => setMaintenanceFormData({...maintenanceFormData, priority: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">
                      <Badge className="bg-green-100 text-green-800">Low</Badge>
                    </SelectItem>
                    <SelectItem value="medium">
                      <Badge className="bg-yellow-100 text-yellow-800">Medium</Badge>
                    </SelectItem>
                    <SelectItem value="high">
                      <Badge className="bg-orange-100 text-orange-800">High</Badge>
                    </SelectItem>
                    <SelectItem value="urgent">
                      <Badge className="bg-red-100 text-red-800">Urgent</Badge>
                    </SelectItem>
                    <SelectItem value="emergency">
                      <Badge className="bg-red-600 text-white">Emergency</Badge>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-gray-700">Description *</label>
              <Textarea
                value={maintenanceFormData.description}
                onChange={(e) => setMaintenanceFormData({...maintenanceFormData, description: e.target.value})}
                placeholder="Describe the maintenance issue or request in detail..."
                rows={4}
                className="mt-1"
              />
            </div>

            {/* Additional Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>Estimated Duration</span>
                </label>
                <Input
                  value={maintenanceFormData.estimated_duration}
                  onChange={(e) => setMaintenanceFormData({...maintenanceFormData, estimated_duration: e.target.value})}
                  placeholder="e.g., 2 hours, 1 day"
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                  <DollarSign className="h-4 w-4" />
                  <span>Cost Estimate</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={maintenanceFormData.cost_estimate}
                  onChange={(e) => setMaintenanceFormData({...maintenanceFormData, cost_estimate: e.target.value})}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Materials Needed */}
            <div>
              <label className="text-sm font-medium text-gray-700">Materials Needed</label>
              <Textarea
                value={maintenanceFormData.materials_needed}
                onChange={(e) => setMaintenanceFormData({...maintenanceFormData, materials_needed: e.target.value})}
                placeholder="List any specific materials, tools, or supplies needed..."
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={resetMaintenanceForm}>
                Cancel
              </Button>
              <Button 
                onClick={handleMaintenanceRequest}
                disabled={!maintenanceFormData.maintenance_type || !maintenanceFormData.description}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Wrench className="h-4 w-4 mr-2" />
                Create Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Room Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Room</DialogTitle>
            <DialogDescription>
              Update room details and bed capacity
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={(e) => { e.preventDefault(); handleEditRoom(); }} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Room Number *</label>
                <Input
                  value={formData.room_number}
                  onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                  placeholder="e.g., 101, A-1"
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Number of Beds *</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.capacity}
                  onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})}
                  placeholder="Number of beds"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Room Gender *</label>
              <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                <SelectTrigger data-testid="edit-room-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="any">Any / Mixed</SelectItem>
                  <SelectItem value="family">Family</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any special notes about this room..."
                rows={3}
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => {
                setShowEditDialog(false);
                setSelectedRoom(null);
                setFormData({ room_number: '', capacity: 1, gender: 'any', notes: '' });
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Edit className="h-4 w-4 mr-2" />
                Update Room
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Room Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Room</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this room? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {roomToDelete && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="space-y-2">
                <p className="font-medium text-red-800">
                  🏠 Room {roomToDelete.room_number}
                </p>
                <p className="text-sm text-red-700">
                  📏 Capacity: {roomToDelete.capacity} residents
                </p>
                <p className="text-sm text-red-700">
                  👥 Current Occupancy: {roomToDelete.current_occupancy}/{roomToDelete.capacity}
                </p>
                {roomToDelete.notes && (
                  <p className="text-sm text-red-600">
                    📝 Notes: {roomToDelete.notes}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              onClick={cancelDeleteRoom}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteRoom}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Room
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomManagement;